import React from 'react';

interface SinglePointReferenceDisplayProps {
  value: number;
  referenceMin: number | null;
  referenceMax: number | null;
  units: string;
  testName: string;
}

export const SinglePointReferenceDisplay: React.FC<SinglePointReferenceDisplayProps> = ({
  value,
  referenceMin,
  referenceMax,
  units,
  testName
}) => {
  // Check if this is a one-sided reference range
  const hasNoUpperLimit = referenceMax === 999 || referenceMax === null;
  const hasNoLowerLimit = referenceMin === null || referenceMin === undefined;
  
  // Handle different range types
  if (hasNoLowerLimit && !hasNoUpperLimit && referenceMax !== null) {
    // Only upper limit (e.g., "< 5")
    const status = value > referenceMax ? 'high' : 'normal';
    const statusColor = status === 'high' ? '#EF4444' : '#10B981';
    const statusText = status === 'high' ? 'Above Normal' : 'Normal';
    const statusIcon = status === 'high' ? '↑' : '✓';
    
    // Calculate position for visualization
    const displayMin = 0; // Start from 0 when no lower limit
    const displayMax = referenceMax * 1.5; // Extend beyond max for visualization
    const displayRange = displayMax - displayMin;
    const refMaxPos = ((referenceMax - displayMin) / displayRange) * 100;
    const valuePos = ((value - displayMin) / displayRange) * 100;
    
    return (
      <div className="bg-white rounded-lg">
        {/* Value display with status - matching regular cards */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" style={{ color: statusColor }}>{value}</span>
              <span className="text-lg text-gray-600">{units}</span>
            </div>
          </div>
          <div 
            className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: status === 'high' ? '#FEE2E2' : '#D1FAE5',
              color: statusColor 
            }}
          >
            <span>{statusIcon}</span>
            <span>{statusText}</span>
          </div>
        </div>
        
        {/* Visual bar - matching regular cards but no red on left */}
        <div className="mb-1">
          <div className="relative h-8 rounded-lg overflow-hidden">
            <div className="absolute inset-0 flex">
              {/* Normal range (green) - from left */}
              <div 
                className="bg-green-400"
                style={{ width: `${refMaxPos}%` }}
              />
              {/* High range (red gradient) */}
              <div 
                className="bg-gradient-to-r from-red-100 to-red-300 flex-1"
              />
            </div>
            
            {/* Value marker */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-black"
              style={{ left: `${Math.min(valuePos, 95)}%` }}
            >
              {/* Arrow indicator */}
              <div 
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid black'
                }}
              />
            </div>
          </div>
          
          {/* Labels below the bar */}
          <div className="relative mt-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div 
                className="absolute"
                style={{ 
                  left: `${refMaxPos}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                &lt;{referenceMax}
              </div>
            </div>
            <div className="h-4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!hasNoLowerLimit && hasNoUpperLimit && referenceMin !== null) {
    // Only lower limit (e.g., "> 1.0" for HDL)
    const status = value < referenceMin ? 'low' : 'normal';
    const statusColor = status === 'low' ? '#3B82F6' : '#10B981';
    const statusText = status === 'low' ? 'Below Normal' : 'Normal';
    const statusIcon = status === 'low' ? '↓' : '✓';
    
    // Calculate position for visualization
    const displayMin = referenceMin * 0.5; // Start display at half the minimum
    const displayMax = Math.max(value, referenceMin) * 1.5; // Extend beyond for visualization
    const displayRange = displayMax - displayMin;
    const refMinPos = ((referenceMin - displayMin) / displayRange) * 100;
    const valuePos = ((value - displayMin) / displayRange) * 100;
    
    return (
      <div className="bg-white rounded-lg">
        {/* Value display with status - matching regular cards */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" style={{ color: statusColor }}>{value}</span>
              <span className="text-lg text-gray-600">{units}</span>
            </div>
          </div>
          <div 
            className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: status === 'low' ? '#DBEAFE' : '#D1FAE5',
              color: statusColor 
            }}
          >
            <span>{statusIcon}</span>
            <span>{statusText}</span>
          </div>
        </div>
        
        {/* Visual bar - matching regular cards but no red on right */}
        <div className="mb-1">
          <div className="relative h-8 rounded-lg overflow-hidden">
            <div className="absolute inset-0 flex">
              {/* Low range (red gradient) */}
              <div 
                className="bg-gradient-to-r from-red-300 to-red-100"
                style={{ width: `${refMinPos}%` }}
              />
              {/* Normal range (green) - extends to the right */}
              <div 
                className="bg-green-400 flex-1"
              />
            </div>
            
            {/* Value marker */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-black"
              style={{ left: `${valuePos}%` }}
            >
              {/* Arrow indicator */}
              <div 
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid black'
                }}
              />
            </div>
          </div>
          
          {/* Labels below the bar */}
          <div className="relative mt-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div 
                className="absolute"
                style={{ 
                  left: `${refMinPos}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                &gt;{referenceMin}
              </div>
            </div>
            <div className="h-4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Both limits present - original logic
  if (referenceMin !== null && referenceMax !== null && !hasNoUpperLimit) {
    // Calculate display range with more padding for better visualization
    const range = referenceMax - referenceMin;
    const padding = range * 0.5; // More padding for better visualization
    const displayMin = Math.min(value, referenceMin) - padding;
    const displayMax = Math.max(value, referenceMax) + padding;
    const displayRange = displayMax - displayMin;
    
    // Calculate positions for display
    const refMinPos = ((referenceMin - displayMin) / displayRange) * 100;
    const refMaxPos = ((referenceMax - displayMin) / displayRange) * 100;
    const valuePos = ((value - displayMin) / displayRange) * 100;
    
    // Determine status
    const status = value < referenceMin ? 'low' : value > referenceMax ? 'high' : 'normal';
    const statusColor = status === 'low' ? '#3B82F6' : status === 'high' ? '#EF4444' : '#10B981';
    const statusText = status === 'low' ? 'Below Normal' : status === 'high' ? 'Above Normal' : 'Normal';
    const statusIcon = status === 'low' ? '↓' : status === 'high' ? '↑' : '✓';
    
    return (
      <div className="bg-white rounded-lg">
        {/* Value display with status */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" style={{ color: statusColor }}>{value}</span>
              <span className="text-lg text-gray-600">{units}</span>
            </div>
          </div>
          <div 
            className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: status === 'low' ? '#DBEAFE' : status === 'high' ? '#FEE2E2' : '#D1FAE5',
              color: statusColor 
            }}
          >
            <span>{statusIcon}</span>
            <span>{statusText}</span>
          </div>
        </div>
        
        {/* Visual bar */}
        <div className="mb-1">
          <div className="relative h-8 rounded-lg overflow-hidden">
            {/* Background gradient - red on edges, green in middle */}
            <div className="absolute inset-0 flex">
              {/* Low range (red gradient) */}
              <div 
                className="bg-gradient-to-r from-red-300 to-red-100"
                style={{ width: `${refMinPos}%` }}
              />
              {/* Normal range (green) */}
              <div 
                className="bg-green-400"
                style={{ width: `${refMaxPos - refMinPos}%` }}
              />
              {/* High range (red gradient) */}
              <div 
                className="bg-gradient-to-r from-red-100 to-red-300 flex-1"
              />
            </div>
            
            {/* Value marker */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-black"
              style={{ left: `${valuePos}%` }}
            >
              {/* Arrow indicator */}
              <div 
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid black'
                }}
              />
            </div>
          </div>
          
          {/* Labels below the bar */}
          <div className="relative mt-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div 
                className="absolute"
                style={{ 
                  left: `${refMinPos}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                {referenceMin}
              </div>
              <div 
                className="absolute"
                style={{ 
                  left: `${refMaxPos}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                {referenceMax}
              </div>
            </div>
            {/* Spacer to maintain height */}
            <div className="h-4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // No reference range available
  return (
    <div className="bg-white rounded-lg">
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-700">{value}</span>
          <span className="text-lg text-gray-600">{units}</span>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          No reference range available
        </div>
      </div>
    </div>
  );
};